import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import SiteLayout from "@/components/layout/SiteLayout";
import Seo from "@/components/base/Seo";
import SafeImage from "@/components/base/SafeImage";
import type { DirectoryLocation } from "@/types/directory";
import { getLocations } from "@/services/directoryService";
import { getLocationFallbackImage } from "@/utils/fallbackImage";

export default function LocationsPage() {
  const [locations, setLocations] = useState<DirectoryLocation[]>([]);

  useEffect(() => {
    let active = true;
    getLocations()
      .then((list) => {
        if (active) setLocations(list);
      })
      .catch(() => {
        if (active) setLocations([]);
      });
    return () => {
      active = false;
    };
  }, []);

  const districts = locations.filter((location) => location.type === "district");
  const towns = locations.filter((location) => location.type === "town");

  return (
    <SiteLayout>
      <Seo
        title="Locations We Cover Across Coastal Karnataka"
        description="Browse businesses by location across South Canara, Udupi and North Canara — down to town and locality level. Pick a district to explore nearby verified businesses."
        keywords="businesses in Mangaluru, Udupi businesses, Karwar businesses, coastal Karnataka locations, local businesses near me"
        path="/locations"
      />
      <section className="border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-[1400px] px-6 py-10 md:py-12">
          <nav className="flex items-center gap-2 text-xs text-foreground-500" aria-label="Breadcrumb">
            <span>Home</span>
            <span className="flex h-3.5 w-3.5 items-center justify-center">
              <i className="ri-arrow-right-s-line text-sm" />
            </span>
            <span className="font-medium text-foreground-700">Locations</span>
          </nav>
          <h1 className="mt-3 font-heading text-2xl font-extrabold text-foreground-950 md:text-4xl">
            Locations we cover
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
            Coastal Bizz Connect covers South Canara, Udupi and North Canara — right down to town and
            locality level. Pick a district to start exploring.
          </p>
        </div>
      </section>

      <section className="mx-auto w-full max-w-[1400px] px-6 py-10 md:py-12">
        <div className="flex flex-col gap-10">
          {districts.map((district) => {
            const districtTowns = towns.filter((town) => town.districtSlug === district.slug);
            return (
              <article
                key={district.id}
                className="overflow-hidden rounded-lg border border-background-200 bg-background-50"
              >
                <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_2fr]">
                  <Link
                    to={`/location/${district.slug}`}
                    className="relative block h-56 w-full overflow-hidden lg:h-full"
                  >
                    <SafeImage
                      src={district.image}
                      fallback={getLocationFallbackImage(district)}
                      alt={`${district.name} district in coastal Karnataka`}
                      title={`${district.name} - Coastal Bizz Connect`}
                      className="h-full w-full object-top"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-primary-950/70 to-transparent lg:bg-gradient-to-r" />
                    <div className="absolute bottom-4 left-4 right-4">
                      <h2 className="font-heading text-xl font-bold text-background-50">
                        {district.name}
                      </h2>
                    </div>
                  </Link>

                  <div className="p-6">
                    <div className="flex flex-wrap items-center gap-4">
                      <p className="flex items-center gap-2 text-sm text-foreground-600">
                        <span className="flex h-4 w-4 items-center justify-center text-secondary-600">
                          <i className="ri-store-3-line text-base" />
                        </span>
                        {district.businessCount.toLocaleString("en-IN")} businesses
                      </p>
                      <p className="flex items-center gap-2 text-sm text-foreground-600">
                        <span className="flex h-4 w-4 items-center justify-center text-secondary-600">
                          <i className="ri-map-pin-2-line text-base" />
                        </span>
                        {districtTowns.length} towns & localities
                      </p>
                    </div>

                    <div className="mt-4 flex flex-wrap gap-2">
                      {districtTowns.map((town) => (
                        <Link
                          key={town.id}
                          to={`/location/${town.slug}`}
                          className="whitespace-nowrap rounded-full bg-background-100 px-3 py-1.5 text-xs font-medium text-foreground-700 transition-colors hover:bg-secondary-100 hover:text-secondary-900"
                        >
                          {town.name}
                        </Link>
                      ))}
                    </div>

                    <Link
                      to={`/location/${district.slug}`}
                      className="mt-5 inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-4 py-2.5 text-sm font-semibold text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700"
                    >
                      Explore {district.name}
                      <span className="flex h-4 w-4 items-center justify-center">
                        <i className="ri-arrow-right-line text-base" />
                      </span>
                    </Link>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </section>
    </SiteLayout>
  );
}